import React, { useState, useEffect } from 'react';
import { Plus, Trash2, MapPin, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import './PincodeManagement.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PincodeManagement = () => {
  const [pincodes, setPincodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [newPincode, setNewPincode] = useState('');
  const [newArea, setNewArea] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Fetch all pincodes
  const fetchPincodes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/pincodes/`);
      setPincodes(response.data || []);
      setError('');
    } catch (err) {
      setError(`Failed to load pincodes: ${err.response?.data?.detail || err.message}`);
      setPincodes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPincodes();
  }, []);

  // Add new pincode
  const handleAddPincode = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!/^\d{6}$/.test(newPincode)) {
      setError('Pincode must be exactly 6 digits');
      return;
    }
    
    if (!newArea.trim()) {
      setError('Area name is required');
      return;
    }
    
    setIsAdding(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.post(
        `${API_URL}/api/pincodes/admin/add`,
        {
          pincode: newPincode,
          area: newArea.trim()
        },
        {
          withCredentials: true
        }
      );
      
      // Add to list
      setPincodes([...pincodes, response.data].sort((a, b) => a.pincode.localeCompare(b.pincode)));
      
      // Reset form
      setNewPincode('');
      setNewArea('');
      setSuccess('Pincode added successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to add pincode';
      setError(errorMessage);
    } finally {
      setIsAdding(false);
    }
  };

  // Delete pincode
  const handleDeletePincode = async (pincode) => {
    if (!window.confirm(`Are you sure you want to delete pincode ${pincode}?`)) {
      return;
    }
    
    setError('');
    setSuccess('');
    
    try {
      await axios.delete(
        `${API_URL}/api/pincodes/admin/delete/${pincode}`,
        {
          withCredentials: true
        }
      );
      
      // Remove from list
      setPincodes(pincodes.filter(p => p.pincode !== pincode));
      setSuccess('Pincode deleted successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete pincode';
      setError(errorMessage);
    }
  };

  return (
    <div className="pincode-management">
      <div className="pincode-header">
        <div>
          <h2>Delivery Pincodes</h2>
          <p className="subtitle">Manage delivery areas for your e-commerce store</p>
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          <div>
            <strong>Error:</strong>
            <div>{error}</div>
            <small style={{fontSize: '0.85em', marginTop: '4px', display: 'block'}}>
              Check browser console (F12) for more details
            </small>
          </div>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <CheckCircle size={20} />
          <span>{success}</span>
        </div>
      )}

      {/* Add Pincode Form */}
      <div className="add-pincode-card">
        <h3>Add New Pincode</h3>
        <form onSubmit={handleAddPincode} className="add-pincode-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="pincode">Pincode *</label>
              <input
                type="text"
                id="pincode"
                placeholder="e.g., 560078"
                value={newPincode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setNewPincode(value);
                }}
                maxLength="6"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="area">Area Name *</label>
              <input
                type="text"
                id="area"
                placeholder="e.g., Koramangala 1st to 4th Block"
                value={newArea}
                onChange={(e) => setNewArea(e.target.value)}
                maxLength="200"
                required
              />
            </div>
            
            <button 
              type="submit" 
              className="btn-primary add-btn"
              disabled={isAdding}
            >
              <Plus size={20} />
              {isAdding ? 'Adding...' : 'Add Pincode'}
            </button>
          </div>
        </form>
      </div>

      {/* Pincodes List */}
      <div className="pincodes-list-card">
        <h3>Current Delivery Areas ({pincodes.length})</h3>
        
        {loading ? (
          <div className="loading-state">Loading pincodes...</div>
        ) : pincodes.length === 0 ? (
          <div className="empty-state">
            <MapPin size={48} strokeWidth={1} />
            <p>No delivery pincodes configured yet</p>
            <p className="empty-subtitle">Add your first pincode above to get started</p>
          </div>
        ) : (
          <div className="pincodes-table-wrapper">
            <table className="pincodes-table">
              <thead>
                <tr>
                  <th>Pincode</th>
                  <th>Area</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pincodes.map((pincode) => (
                  <tr key={pincode.pincode}>
                    <td>
                      <div className="pincode-cell">
                        <MapPin size={16} />
                        <strong>{pincode.pincode}</strong>
                      </div>
                    </td>
                    <td>{pincode.area}</td>
                    <td>
                      <button
                        onClick={() => handleDeletePincode(pincode.pincode)}
                        className="btn-icon btn-delete"
                        title="Delete pincode"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PincodeManagement;
